import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { dismissAll, isConnectionFailure } from '../../lib/errorNotices';
import { clearOfflineCopies } from '../../lib/offlineCopies';
import { isOnline } from '../../lib/online';
import {
  isAuthError,
  queryClient as appQueryClient,
  trpc,
  type RouterOutputs,
} from '../../lib/trpc';

/**
 * Who is signed in.
 *
 * One query, `auth.me`, is the single client-side answer to that. Everything else — the rail,
 * the guard, the users page — reads it rather than keeping its own copy, so there is no way
 * for two parts of the screen to disagree about which organisation you are in. That matters
 * more here than in most apps: acting in the wrong org is the failure mode this whole product
 * is built to make impossible.
 */

export type Session = RouterOutputs['auth']['me'];

/**
 * `retry: false` and a short staleness window.
 *
 * Not being logged in is a normal answer, not a transient failure — retrying it delays the
 * login redirect by seconds while the user stares at nothing. The 60s window keeps navigation
 * from re-asking on every route change while still noticing a session revoked elsewhere
 * reasonably soon; anything genuinely urgent (a suspension, a role change) invalidates the
 * session server-side and the next request fails regardless of what this cache says.
 */
export function sessionQueryOptions() {
  return trpc.auth.me.queryOptions(undefined, { retry: false, staleTime: 60_000 });
}

/**
 * Resolve the session for a route guard. `null` means "not signed in", not "it broke".
 *
 * Only a genuine 401 becomes `null`. A network failure or a 500 is rethrown so the router
 * shows an error rather than bouncing the user to a login page that will also fail to load —
 * "log in again" is infuriating advice when the API is simply down.
 *
 * ── WITH NO CONNECTION AT ALL, THE LAST SESSION THIS DEVICE SAW ──────────────────────────────
 *
 * So the installed app opens in a dead zone, onto the inspections kept on the phone. It grants
 * NOTHING: every request still carries the cookie and the server still decides, so all a stale
 * session can do is draw the shell around data already on the device. Only a connection failure
 * reaches it — a refusal is a refusal, and forgets it. Checked before asking when the browser is
 * certain it is offline, because React Query pauses an offline request rather than failing it,
 * and a guard awaiting a paused request would hold the whole app on a blank screen until signal.
 */
export async function resolveSession(client: QueryClient): Promise<Session | null> {
  const fallBack = () => {
    const kept = rememberedSession();
    if (kept !== null) client.setQueryData(sessionQueryOptions().queryKey, kept);
    return kept;
  };

  if (!isOnline() && client.getQueryData(sessionQueryOptions().queryKey) === undefined) {
    const kept = fallBack();
    if (kept !== null) return kept;
    throw new Error('There is no connection, and nobody has signed in on this device yet.');
  }

  try {
    const session = await client.ensureQueryData(sessionQueryOptions());
    rememberSession(session);
    return session;
  } catch (error) {
    if (isAuthError(error)) {
      forgetDevice();
      return null;
    }
    if (isConnectionFailure(error)) {
      const kept = fallBack();
      if (kept !== null) return kept;
    }
    throw error;
  }
}

/*
 * The remembered session. `localStorage`, not IndexedDB: it is one small record, read
 * synchronously by the guard before anything renders.
 */
const SESSION_KEY = 'promanage:offline-session';

function rememberSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // No storage: the app simply cannot open offline, as before.
  }
}

/** Parsed, not cast — a session written by an older build is only used if it still has the shape. */
function rememberedSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const { user, orgId } = value as { user?: { id?: unknown }; orgId?: unknown };
    if (typeof user?.id !== 'string' || typeof orgId !== 'string') return null;
    return value as Session;
  } catch {
    return null;
  }
}

/**
 * Forget what this device holds for offline use: the remembered session and every kept record.
 *
 * Called on sign-out and whenever the server refuses the session. Unsent inspection DRAFTS are not
 * touched — a session lapsing in a dead zone must not take the morning's findings with it
 * (docs/SECURITY.md); they are tagged to their author and only ever sent for them.
 */
export function forgetDevice(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing stored.
  }
  clearOfflineCopies();
}

/** The signed-in user, once the guard has established there is one. */
export function useSession(): Session | undefined {
  return useQuery(sessionQueryOptions()).data;
}

/**
 * Every organisation this login can act in.
 *
 * Stated once because three callers want it: the rail's brand tile, AccountMenu's switcher,
 * and the root guard, which warms it so the shell's first paint names the agency instead of a
 * placeholder. React Query dedupes by key, so that is one request between them, not three.
 */
export function orgListQueryOptions() {
  return trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 });
}

/**
 * The organisation the current session is acting in, by name.
 *
 * Reads `auth.organisations` rather than duplicating the name onto `auth.me`.
 */
export function useCurrentOrg(): RouterOutputs['auth']['organisations'][number] | undefined {
  const orgs = useQuery(orgListQueryOptions());
  return orgs.data?.find((org) => org.isCurrent);
}

/**
 * Forget everything after the identity changes.
 *
 * Cached queries belong to whoever was signed in when they were fetched. Leaving them in
 * place across a login, a logout or an org switch would show one organisation's data to the
 * next session for as long as it stayed fresh — a tenant leak created entirely on the client,
 * where RLS cannot help. `clear()` rather than `invalidateQueries()`: invalidation keeps
 * serving the stale value while it refetches, which is the exact frame that must not exist.
 */
export function resetClientCache(client: QueryClient = appQueryClient): void {
  client.clear();
  // And the error notices with it. They describe what THIS identity was refused, and the next
  // person to sign in at this desk has no business reading why the last one's payment failed.
  dismissAll();
}

/**
 * Start again in the organisation the session has just switched to.
 *
 * ── A PAGE LOAD, NOT A NAVIGATION ────────────────────────────────────────────────────────────
 *
 * This used to clear the cache and `navigate` to the dashboard, and the screen went on showing
 * the organisation that had just been left. Two reasons, and either was enough. Clearing the
 * cache removes the queries; it does not tell the components reading them, so everything already
 * on screen kept the last result it was handed. And the navigation was to `/` — which, for
 * somebody switching from the dashboard, is where they already were, so nothing remounted and
 * nothing asked again. The numbers of one agency stayed up under the name of another, which is
 * the failure this product is built to make impossible.
 *
 * A load cannot leave anything behind, and the cache is not the only thing that could: a route's
 * loader data, an unsaved grid on the map, the chosen portfolio scope, a half-filled form — all
 * of it belongs to the organisation that was left. Finding and resetting each is a list somebody
 * has to keep complete for ever; discarding the page is not.
 *
 * It costs one reload on an action taken a few times a day, and the session check is already in
 * flight before the bundle parses (`earlySession.ts`).
 *
 * `replace`, so Back does not return to a page of the old organisation's URLs. The cache is still
 * cleared first: the reload is not instant, and nothing of the old organisation should be
 * readable in the frames before it lands.
 */
export function enterOrganisation(result: { mfaRequired: boolean }): void {
  resetClientCache();
  // Asked by the same rule as login: when the user has an authenticator, or their role in the
  // new organisation requires one. `/mfa` works out whether to verify or enrol.
  window.location.replace(`${import.meta.env.BASE_URL}${result.mfaRequired ? 'mfa' : ''}`);
}

export function useResetClientCache(): () => void {
  const client = useQueryClient();
  return () => {
    resetClientCache(client);
  };
}
